import React, { useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import PlusIcon from '~/components/icons/PlusIcon';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import type {
  GuardrailRuleCreateParams,
  GuardrailRuleInfo,
  GuardrailSupportedEventsResponse,
} from '~/server_shared/openapi/data-contracts';
import { isLegacyOrgFeatureEnabled } from '~/Utils';

import type { GuardrailRulePresetContext } from '../../../AutomaticRulesConfiguration/Guardrails/AddOrEditGuardrailRuleDialog';
import AddOrEditGuardrailRuleDialog from '../../../AutomaticRulesConfiguration/Guardrails/AddOrEditGuardrailRuleDialog';
import {
  createGuardrailRule,
  deleteGuardrailRule,
  setGuardrailRuleEnabled,
  updateGuardrailRule,
} from '../../../AutomaticRulesConfiguration/Guardrails/api/guardrailRulesApi';
import { GuardrailRulesTable } from '../../../AutomaticRulesConfiguration/Guardrails/components/GuardrailRulesTable';
import type { SysconfigOrganization } from '../../../AutomaticRulesConfiguration/Guardrails/types';

import { isClaimActionGuardrail } from './claimActionGuardrails';
import type { GuardrailRequiredCondition, ScopeRow } from './types';

interface ClaimActionGuardrailsSectionProps {
  /** The events this action reports; the first is the one a new guardrail is pre-filled with.
   *  Empty when the action has no guardrail-authorable event. */
  guardrailEventKeys?: string[] | null;
  /** Conditions every guardrail on the action carries; seeded locked into each one authored here. */
  guardrailsRequiredConditions?: GuardrailRequiredCondition[] | null;
  actionDisplayName: string;
  scope: ScopeRow;
  /** Raised while a guardrail write is in flight, so the dialog hosting this section can hold its
   *  own close and Save for it — a guardrail rule is written on its own request, which the host's
   *  saving flag knows nothing about. Required: a host that renders this section and does not hold
   *  its close can be dismissed mid-write. */
  onProcessingChange: (isProcessing: boolean) => void;
}

const ClaimActionGuardrailsSection: React.FC<ClaimActionGuardrailsSectionProps> = ({
  guardrailEventKeys,
  guardrailsRequiredConditions,
  actionDisplayName,
  scope,
  onProcessingChange,
}) => {
  // Same source the sibling Guardrails screen reads; this section renders inside the same
  // sysconfig subtree, so the target organization comes from the context rather than a prop.
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const organizationId = organization.id;
  const isGuardrailsEngineEnabled = !isLegacyOrgFeatureEnabled(
    organization,
    LEGACY_ORG_FEATURES.HOSPITALITY_NO_GUARDRAILS
  );
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GuardrailRuleInfo | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // A hospitality-only organization gets neither the Guardrails screen nor the backend evaluation
  // (`events_manager` skips it), so there this section would offer an Add flow whose rules are
  // never applied. Nothing is rendered and nothing is fetched.
  const isSectionEnabled = isGuardrailsEngineEnabled && !isEmpty(guardrailEventKeys);

  const {
    isLoading: isLoadingRules,
    isError: isErrorRules,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher<GuardrailRuleInfo[]>(
    `/api/v1/organizations/${organizationId}/guardrails/rules`,
    null,
    isSectionEnabled
  );

  const {
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
    data: supportedEventsResponse,
  } = useDataFetcher<GuardrailSupportedEventsResponse>(
    `/api/v1/organizations/${organizationId}/guardrails/supported_events`,
    null,
    isSectionEnabled
  );

  const actionRules = useMemo(
    () =>
      (rules ?? []).filter((rule) =>
        isClaimActionGuardrail(rule, guardrailEventKeys, scope, guardrailsRequiredConditions)
      ),
    [rules, guardrailEventKeys, scope, guardrailsRequiredConditions]
  );

  const presetContext: GuardrailRulePresetContext = {
    triggerEvents: guardrailEventKeys ?? [],
    subOrganizationIds: scope.sub_organization_id === null ? [] : [scope.sub_organization_id],
    lobs: [scope.lob],
    isAllLobs: false,
    requiredConditions: guardrailsRequiredConditions ?? [],
  };

  const handleCloseDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  // Every guardrail write goes through here, so it is also the one place that has to tell the host
  // dialog a request is outstanding — including the list reload, which is what makes the new state
  // visible.
  const withProcessing = async (fn: () => Promise<void>) => {
    setIsProcessing(true);
    onProcessingChange(true);
    try {
      await fn();
      await reloadRules();
    } finally {
      setIsProcessing(false);
      onProcessingChange(false);
    }
  };

  const handleSubmitRule = async (values: GuardrailRuleCreateParams) => {
    await withProcessing(async () => {
      if (ruleToEdit) {
        await updateGuardrailRule(organizationId, ruleToEdit.id, values);
      } else {
        await createGuardrailRule(organizationId, values);
      }
    });
  };

  // Delete/enable are invoked fire-and-forget from the table (ThreeDotsMenu / Switch), so swallow
  // after the service has already toasted — otherwise the rethrow becomes an unhandled rejection.
  // The create/update path keeps rethrowing so the dialog's Formik catch can reopen.
  const handleDeleteRule = async (rule: GuardrailRuleInfo) => {
    try {
      await withProcessing(() => deleteGuardrailRule(organizationId, rule.id));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEnabledChanged = async (rule: GuardrailRuleInfo) => {
    try {
      await withProcessing(() => setGuardrailRuleEnabled(organizationId, rule.id, !rule.is_enabled));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEditRuleClicked = (rule: GuardrailRuleInfo) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  if (!isSectionEnabled) {
    return null;
  }

  if (isLoadingRules || isLoadingEvents || isErrorRules || isErrorEvents) {
    // Column count tracks GuardrailRulesTable, which adds a Sub Organizations column for
    // sub-org-enabled organizations.
    return (
      <SkeletonTable
        rowsCount={3}
        columnsCount={organization.sub_organizations_enabled ? 6 : 5}
        isError={isErrorRules || isErrorEvents}
      />
    );
  }

  return (
    <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
      <div className="flex items-center justify-between pb-12">
        <div className="flex items-center gap-8">
          <Heading variant={Heading.TYPES.H3}>Guardrails</Heading>
          <Chip label={String(actionRules.length)} size="small" />
        </div>
        <Button
          color="primary"
          disabled={isProcessing}
          onClick={() => setIsRuleDialogOpen(true)}
          startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
        >
          Add Guardrail
        </Button>
      </div>
      <AlertBanner
        className="mb-12"
        alertType="info"
        note={`Rules from the guardrails engine, scoped to ${actionDisplayName}. Evaluated on every attempted call, on top of automatic execution. A matching rule stops the call and the agent cannot override it: a Block rule returns the rule's message to the agent, which may resolve the condition and retry, while a Route to Human rule ends the run and raises the action for a person. Guardrails are saved as you edit them, separately from this dialog's Save.`}
        withIcon
      />
      {/* The Guardrails screen's own table, not a second one: same columns, same row menu, and the
          same Enable switch, so a rule reads identically wherever the admin meets it. */}
      <GuardrailRulesTable
        organization={organization}
        rules={actionRules}
        isProcessing={isProcessing}
        emptyStateText="No guardrails configured for this action."
        onEditRule={handleEditRuleClicked}
        onDeleteRule={handleDeleteRule}
        onEnabledChanged={handleEnabledChanged}
      />
      {isRuleDialogOpen ? (
        <AddOrEditGuardrailRuleDialog
          supportedEvents={supportedEventsResponse?.events}
          onCancel={handleCloseDialog}
          onSubmit={handleSubmitRule}
          rule={ruleToEdit}
          presetContext={presetContext}
        />
      ) : null}
    </div>
  );
};

export default ClaimActionGuardrailsSection;
